import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { AxiosError, HttpStatusCode } from 'axios'
import { Api } from '@apiTypes'
import { clearAuthDataAndRedirect, hasRefreshToken, refreshTokenHelper, setHeaders, shouldLogoutOn401 } from '@/auth/utils'
import { flattenObject } from '@/utils/common.utils'
import { CARRIER_PORTAL_TOKEN } from '@/utils/const'

const api = new Api({ paramsSerializer: { indexes: null } })
const apiAxiosInstance = api.instance
let _refreshTokenRequest: Promise<unknown> | null = null

const CARRIER_PORTAL_ENDPOINTS = [
  /\/api\/(?:v\d+\/)?carrier\//,
  /\/api\/(?:v\d+\/)?by-email\//,
]

function isCarrierPortalEndpoint(url = '') {
  return CARRIER_PORTAL_ENDPOINTS.some(rx => rx.test(url))
}

// This is fix for development env, if you want to access via FE port (currently 5173) directly
if (!!window.location.port && window.location.port !== '80') {
  const newBaseUrl = `http://127.0.0.1:8000/`

  console.warn('[AXIOS] Changing API base URL', newBaseUrl)
  apiAxiosInstance.defaults.baseURL = newBaseUrl
}
apiAxiosInstance.interceptors.request.use(config => {
  if (import.meta.env.VITE_AXIOS_DEBUG === 'true')
    console.log('[AXIOS] Request Interceptors', config)

  // Carrier portal token (OTP hash) injection for Carrier portal endpoints
  if (isCarrierPortalEndpoint(config.url || '')) {
    const carrierToken = localStorage.getItem(CARRIER_PORTAL_TOKEN)
    if (carrierToken) {
      config.headers = config.headers || {}
      config.headers['X-Carrier-Token'] = carrierToken
    }
  }

  setHeaders(config)

  return config
}, (error: AxiosError) => {
  console.error('[AXIOS] Request Interceptors Error', error)

  return Promise.reject(error)
})

apiAxiosInstance.interceptors.response.use(response => {
  if (import.meta.env.VITE_AXIOS_DEBUG === 'true')
    console.log('[AXIOS] Response Interceptors', response)

  return response
}, async (err: AxiosError) => {
  console.error('[AXIOS] Response Interceptors Error', err)

  return handleAxiosError(err)
})

async function retryRequestWithRefreshedToken(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  setHeaders(config)
  config.skipAuthRefresh = true

  return apiAxiosInstance.request(config)
}

async function handleRefreshTokenError(refreshTokenError: unknown, config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  if (!(refreshTokenError instanceof AxiosError && refreshTokenError.response?.status === HttpStatusCode.Unauthorized))
    throw refreshTokenError

  // Try one more time because token can be refreshed in another tab
  try {
    return await retryRequestWithRefreshedToken(config)
  }
  catch (error) {
    if (refreshTokenError.response?.status === HttpStatusCode.Unauthorized)
      clearAuthDataAndRedirect()
    throw error
  }
}

async function handle401WithRefreshToken(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  _refreshTokenRequest ??= refreshTokenHelper()

  try {
    await _refreshTokenRequest

    return await retryRequestWithRefreshedToken(config)
  }
  catch (refreshTokenError: unknown) {
    return await handleRefreshTokenError(refreshTokenError, config)
  }
  finally {
    _refreshTokenRequest = null
  }
}

async function handleAxiosError(err: AxiosError): Promise<AxiosResponse> {
  switch (err.response?.status) {
    case HttpStatusCode.BadRequest:
      if (isRecord(err.response?.data))
        err.response.data = flattenObject(err.response.data)
      break
    case HttpStatusCode.Unauthorized: {
      const config = err.config!

      if (hasRefreshToken() && !config.skipAuthRefresh)
        return await handle401WithRefreshToken(config)
      else if (!config.skipLogout && shouldLogoutOn401()) {
        clearAuthDataAndRedirect()
      }
      break
    }
  }

  throw err
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

apiAxiosInstance.defaults.xsrfHeaderName = 'X-CSRFToken'
apiAxiosInstance.defaults.xsrfCookieName = 'csrftoken'

export const axiosInstance = apiAxiosInstance
export default api
